import { useState, useRef, useEffect } from 'react';

import { calculateSlideshowDuration } from '../helpers';

/**
 * Slideshow Playback hook.
 *
 * @param {Object} options The options.
 * @param {Array} [options.slideshowPhotos] The collection of slideshow photos
 * @param {Object} [options.slideshowPhoto] The current active slideshow photo
 * @param {Number} [options.slideshowPhotoIdx] The current active slideshow photo index
 * @param {Number} [options.nextPhotoId] The ID of the next photo to be displayed
 * @param {Number} [options.slideDurationMs] The time each slide lasts (in ms)
 * @param {Number} [options.slideshowDuration] The time it takes for the slideshow to complete (in ms)
 * @param {Number} [options.intervalValue] The amount of time to wait before updating the elapsed time (in ms)
 * @param {Boolean} [options.restartOnEnd] Whether to stop at the last photo, or restart when the slideshow ends
 * @param {Boolean} [options.autoPlay] Whether to start playing after loading, or wait for user input to do so
 * @param {Boolean} [options.allowDeselect] Whether to allow deselecting (having no active slide)
 * @param {function} [options.onChangePhoto] Function to call once the time for the next photo to be displayed comes
 */
const useSlideshowPlayback = ({
  slideshowPhotos,
  slideshowPhoto: currentPhoto,
  slideshowPhotoIdx: currentPhotoIdx,
  nextPhotoId,
  slideDurationMs,
  slideshowDuration,
  intervalValue = 1000,
  restartOnEnd = true,
  autoPlay = true,
  allowDeselect = false,
  onChangePhoto,
}) => {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [skipFadeIn, setSkipFadein] = useState(!autoPlay);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [slideDuration, setSlideDuration] = useState(slideDurationMs);
  const [slideRemainingTime, setSlideRemainingTime] = useState(slideDurationMs);

  const counter = useRef(null);

  const onPlaybackStop = () => {
    setIsPlaying(false);
    clearInterval(counter.current);
    counter.current = null;
  };

  const onPlaybackStart = () => {
    // If we have no photos, reset everything and stop
    if ((slideshowPhotos?.length ?? 0) === 0) {
      onPlaybackStop();
      return;
    }
    // If this is the case when we hit play, then we must focus on resetting the photos to 1st one.
    if (elapsedTime === slideshowDuration || !currentPhoto) {
      setElapsedTime(0);
      const firstPhoto = slideshowPhotos[0];
      const { asset_image, photo } = firstPhoto;
      setSlideDuration(slideDurationMs);
      setSlideRemainingTime(slideDurationMs);
      // history.replace(`${basePath}/${id}`);
      onChangePhoto({ nextPhotoId: asset_image?.id ?? photo?.id });
      setIsPlaying(true);
      return;
    }

    counter.current = setInterval(() => {
      setElapsedTime(elapsed => elapsed + intervalValue);
      setSlideRemainingTime(remaining => remaining - intervalValue);
    }, intervalValue);
    setIsPlaying(true);
  };

  const onThumbnailClick = ({ photoId, photoIdx }) => {
    onPlaybackStop();
    // Ignore the changes to elapsed time and history if we click on the same slide
    if (!allowDeselect && photoId === currentPhoto?.photo?.id) {
      return;
    }

    setSkipFadein(true);
    if (typeof photoIdx === 'undefined') {
      setElapsedTime(0);
    } else {
      setElapsedTime(
        calculateSlideshowDuration(slideshowPhotos, slideDurationMs, photoIdx),
      );
    }

    onChangePhoto({ nextPhotoId: photoId });
  };

  // Basically reset elapsedTime, slideDuration, and slideRemainingTime
  // to their defaults if we change the slideDurationMs. This is intended
  // to avoid having awkward situations where the user is playing around
  // with the delay slider all while the slideshow is playing.
  useEffect(() => {
    setElapsedTime(
      slideshowPhotos && currentPhotoIdx
        ? calculateSlideshowDuration(
            slideshowPhotos,
            slideDurationMs,
            currentPhotoIdx,
          )
        : 0,
    );
    setSlideDuration(slideDurationMs);
    setSlideRemainingTime(slideDurationMs);
  }, [slideDurationMs]);

  useEffect(() => {
    if (currentPhoto) {
      clearInterval(counter.current);
      counter.current = null;
      setSlideDuration(slideDurationMs);
      setSlideRemainingTime(slideDurationMs);

      if (isPlaying) {
        setSkipFadein(false);
        onPlaybackStart();
      }
    }
  }, [currentPhoto]);

  useEffect(() => {
    if (slideRemainingTime === 0 && isPlaying && nextPhotoId) {
      // Set the Photo remaining time to a value other than 0
      // since having a 0 here will make it skip the other slide
      // before the useEffect above takes place and updates it
      setSlideRemainingTime(-1);
      // history.replace(`${basePath}/${nextSlideId}`);
      onChangePhoto({ nextPhotoId });
    }
  }, [slideRemainingTime, nextPhotoId]);

  useEffect(() => {
    if (elapsedTime === slideshowDuration && !restartOnEnd) {
      onPlaybackStop();
    } else if (elapsedTime === slideshowDuration && restartOnEnd) {
      onPlaybackStart();
      // This should never happen, but safeguard against it just in case
    } else if (elapsedTime > slideshowDuration) {
      clearInterval(counter.current);
      counter.current = null;
      setElapsedTime(slideshowDuration);
    }
  }, [elapsedTime]);

  useEffect(() => {
    // Ignore this when the currentPhotoIndex = 0
    // This is done to avoid stopping on reset, where
    // the index (0) would reset elapsedTime and possibly
    // break the interaction of the useEffect above. This,
    // however, will not be the case if the slideshow is not
    // playing.
    if (currentPhotoIdx || !isPlaying) {
      setElapsedTime(
        calculateSlideshowDuration(
          slideshowPhotos,
          slideDurationMs,
          currentPhotoIdx,
        ),
      );
    }
  }, [slideshowPhotos, currentPhotoIdx]);

  return {
    isPlaying,
    elapsedTime,
    slideDuration,
    slideRemainingTime,
    slideshowDuration,
    skipFadeIn,
    onThumbnailClick,
    onPlaybackStart,
    onPlaybackStop,
  };
};

export default useSlideshowPlayback;
